// Rename a catalog item's SKU, keeping every old string working.
//
// WHY THIS IS ITS OWN DOOR, and not a field in the Edit form
// `sku` is refused by PATCH (`sku_immutable`) and always will be. Renaming is
// not editing a field: it retires a string, rewrites a denormalized copy in
// `batch_assignments`, and leaves audit behind. Putting it in the metadata form
// would make it something you could do by accident while fixing a typo in a
// description.
//
// It is admin-only, tighter than the Edit button beside it (operations|admin),
// because the string being changed is printed on physical labels and embedded
// in board serial numbers.
//
// The reassurance is the whole point of the copy here. People will not use this
// unless they believe the shelf still works afterwards, and the honest answer is
// that it does — every lookup falls through to the retired string.

import React, { useMemo, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Reveal from '../components/ui/Reveal';
import { Alert } from '../components/ui/Alert';
import { Field } from '../components/ui/Field';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import DrawerShell from '../Receiving/DrawerShell';
import { skuLooksMintable, BRAND_PREFIXES } from './skuGrammar';

interface RenameResult {
  row: { id: number; sku: string };
  previous_sku: string;
  batch_assignments_moved: number;
  restored_from_alias: boolean;
}

interface Props {
  itemId: number;
  currentSku: string;
  onClose: () => void;
  onRenamed: (result: RenameResult) => void;
}

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const RenameSkuModal: React.FC<Props> = ({ itemId, currentSku, onClose, onRenamed }) => {
  const api = useApi();
  const toast = useToast();
  const [sku, setSku] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmed = sku.trim().toUpperCase();
  // Mirrors the server's own rules so the refusal happens under the field
  // rather than after a round trip.
  const problem = useMemo(() => {
    if (!trimmed) return null;
    if (trimmed === currentSku) return 'That is already this item\'s SKU.';
    if (!skuLooksMintable(trimmed)) {
      return `Must start with ${BRAND_PREFIXES.join('- or ')}- and be 50 characters or fewer.`;
    }
    return null;
  }, [trimmed, currentSku]);

  const canSubmit = !!trimmed && !problem && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const result = (await api.post(`/parts-catalog/${itemId}/rename`, {
        body: { sku: trimmed, reason: reason.trim() || null },
      })) as RenameResult;
      toast.success(
        result.batch_assignments_moved > 0
          ? `Renamed to ${result.row.sku}. ${result.batch_assignments_moved} board batch${
              result.batch_assignments_moved === 1 ? '' : 'es'
            } moved with it.`
          : `Renamed to ${result.row.sku}. ${currentSku} still scans.`
      );
      onRenamed(result);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Rename failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell title="Rename SKU" onClose={onClose} dirty={trimmed !== '' || reason.trim() !== ''}>
      <form onSubmit={submit} className="space-y-4">
        <Alert tone="info" live={false}>
          <span className="font-mono text-primary">{currentSku}</span> keeps working. It
          is kept as a former name, and scanning or typing it will still find this item
          and tell you what it is called now. Nothing on a shelf needs relabeling.
        </Alert>

        <label className="block">
          <span className={labelClass}>New SKU</span>
          <Field
            error={!!problem}
            className="font-mono"
            value={sku}
            onChange={(e) => setSku(e.target.value.toUpperCase())}
            placeholder={`e.g. ${currentSku.startsWith('FF-') ? currentSku.replace(/^FF-/, 'SD-') : currentSku}`}
            autoFocus
            autoComplete="off"
          />
          <Reveal show={!!problem}><Alert variant="text" live={false} className="mt-1.5 text-xs">{problem}</Alert></Reveal>
        </label>

        <label className="block">
          <span className={labelClass}>
            Why
            <span className="ml-2 normal-case tracking-normal text-on-surface-variant">(optional)</span>
          </span>
          <Field
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. minted under the wrong brand on Swarm"
          />
          <p className="text-xs text-on-surface-variant mt-1.5 leading-snug">
            Kept with the old name. Worth a line: the next person to look will want to know
            whether this was a correction or a convention change.
          </p>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={buttonClass('filled')}
          >
            {saving ? 'Renaming…' : 'Rename'}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
};

export default RenameSkuModal;
